import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const API = 'https://api.cpms-srlkevcharge.com';

const COLORS = ['#22c55e', '#2a8fd4', '#ef4444'];

function formatDuration(secs) {
  if (!secs) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Math.floor((Date.now() - new Date(dateStr + 'Z')) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(dateStr + 'Z').toLocaleDateString('en-GB');
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d + 'Z').toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(30);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/summary`, {
        params: filter === 'all' ? {} : { days: filter }
      });
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch report', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, [filter]);

  const statusChartData = data ? [
    { name: 'Completed', value: data.status_breakdown.Completed },
    { name: 'Active', value: data.status_breakdown.Active },
    { name: 'Interrupted', value: data.status_breakdown.Interrupted },
  ].filter(d => d.value > 0) : [];

  if (loading) return <div className="loading">Loading reports...</div>;
  if (!data) return <div className="loading">No data available</div>;

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1>Reports & Analytics</h1>
          <p>Network performance overview</p>
        </div>

        {/* Filter buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          {[{ label: 'Last 7 days', value: 7 }, { label: 'Last 30 days', value: 30 }, { label: 'All time', value: 'all' }].map(f => (
            <button
              key={f.value}
              className={`btn ${filter === f.value ? 'btn-primary' : 'btn-ghost'}`}
              style={{ padding: '7px 14px', fontSize: 13 }}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Key Metrics ── */}
      <div className="stat-bar" style={{ marginBottom: 24 }}>
        <div className="card stat-card">
          <div className="stat-label">Total Sessions</div>
          <div className="stat-number total">{data.metrics.total_sessions}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Total Charging Time</div>
          <div className="stat-number charging" style={{ fontSize: 26 }}>{data.metrics.total_hours}h</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Active Chargers</div>
          <div className="stat-number available">{data.metrics.active_chargers}</div>
        </div>
        <div className="card stat-card">
          <div className="stat-label">Avg Session Duration</div>
          <div className="stat-number total" style={{ fontSize: 26 }}>{formatDuration(data.metrics.avg_duration_seconds)}</div>
        </div>
      </div>

      {/* ── Charts Row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 24 }}>

        {/* Sessions over time bar chart */}
        <div className="card" style={{ padding: 20 }}>
          <h2 style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 20 }}>Sessions Over Time</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.sessions_chart} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                interval={filter === 7 ? 0 : Math.floor(data.sessions_chart.length / 7)}
              />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Bar dataKey="sessions" fill="#1d6fa4" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Session status doughnut */}
        <div className="card" style={{ padding: 20 }}>
          <h2 style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 20 }}>Session Status</h2>
          {statusChartData.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 0' }}>
              <p>No session data yet</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={statusChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {statusChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={(value) => <span style={{ fontSize: 12, color: '#64748b' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Charger Performance Table ── */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="section-header">
          <h2>Charger Performance</h2>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{data.charger_performance.length} chargers</span>
        </div>
        <div className="table-wrap">
          {data.charger_performance.length === 0 ? (
            <div className="empty-state">
              <h3>No data yet</h3>
              <p>Charger performance will appear here once sessions have been completed.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Charger</th>
                  <th>Location</th>
                  <th>Total Sessions</th>
                  <th>Total Charging Time</th>
                  <th>Avg Duration</th>
                  <th>Last Active</th>
                </tr>
              </thead>
              <tbody>
                {data.charger_performance.map((c, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{c.charger_name}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{c.location}</td>
                    <td>{c.total_sessions}</td>
                    <td>{c.total_hours}h</td>
                    <td>{formatDuration(c.avg_duration_seconds)}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{timeAgo(c.last_active)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Recent Activity ── */}
      <div className="card">
        <div className="section-header">
          <h2>Recent Activity</h2>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Last 10 completed sessions</span>
        </div>
        <div className="table-wrap">
          {data.recent_activity.length === 0 ? (
            <div className="empty-state">
              <h3>No recent activity</h3>
              <p>Completed sessions will appear here.</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Charger</th>
                  <th>Location</th>
                  <th>Started</th>
                  <th>Ended</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_activity.map((s, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{s.charger_name}</td>
                    <td style={{ color: 'var(--text-muted)' }}>{s.location}</td>
                    <td>{formatDate(s.start_time)}</td>
                    <td>{formatDate(s.end_time)}</td>
                    <td>{formatDuration(s.duration_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}